import { useCallback, useMemo, useRef, useState } from 'react';

import { useSearchLeadsMutation } from '@app/api/Leads';
import { useUserPrint } from '@app/hooks';
import { useLatestRegistryResultBridge } from '@app/pages/Leads/model';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { bigNumberToString } from '@app/utils/bigNumberToString';
import { clogLeadsRegistryDebug } from '@app/utils/clog';

import { buildLeadsRegistrySearchFilter } from '../model/adapters/buildLeadsRegistrySearchFilter';
import { buildLeadsRegistrySearchSignature } from '../model/adapters/buildLeadsRegistrySearchSignature';
import { referenceStableMergeLeads, normalizeLeadsPortionSize } from '../utils';

import { useLeadsRegistryFilters } from './useLeadsRegistryFilters';
import { useLeadsRegistrySearchEffects } from './useLeadsRegistrySearchEffects';
import { useLeadsRegistrySearchIdCleanup } from './useLeadsRegistrySearchIdCleanup';
import { useLeadsRegistrySettings } from './useLeadsRegistrySettings';

import type { LeadsRegistryTableLoadingMode } from '../model/types';

interface RunLeadsRegistrySearchParams {
  maxItems: number;
  isNextPage: boolean;
}

// Registry search ownership for Leads. It runs `SearchLeads`, protects
// against out-of-order responses, feeds the shared latest-result bridge, and
// queues discarded stale `SearchId`s for server-side group cleanup.
export const useLeadsRegistrySearch = () => {
  const { params } = useUserPrint();
  const activeBranchId = params.ActiveBranchId;
  // TEMP issue/10291 — организация нужна, чтобы на проде писать логи только
  // для GRUSHKA и BUSINESS_SOLUTION.
  const database = params.Database;
  const { showNotification } = useNotification();
  const { loadBatchSize } = useLeadsRegistrySettings();
  const { filters } = useLeadsRegistryFilters();
  const [searchLeads] = useSearchLeadsMutation();

  const [pendingDeleteSearchIds, setPendingDeleteSearchIds] = useState<
    string[]
  >([]);

  const {
    leads,
    totalCount,
    isLoading,
    isNextPageLoading,
    hasAcceptedResult,
    hasNextPage,
    isStale,
    refreshRequestId,
    searchSignature: acceptedSearchSignature,
    requestedMaxSize,
    acceptLatestResult,
    requestNextPage,
    resetLatestResult,
    setLoading,
    setNextPageLoading,
  } = useLatestRegistryResultBridge();

  const requestIdRef = useRef(0);

  const queueStaleSearchId = useCallback((searchId: string) => {
    setPendingDeleteSearchIds((prev) =>
      prev.includes(searchId) ? prev : [...prev, searchId]
    );
  }, []);

  const cleanedSearchIdsHandler = useCallback((cleanedIds: string[]) => {
    setPendingDeleteSearchIds((prev) =>
      prev.filter((searchId) => !cleanedIds.includes(searchId))
    );
  }, []);

  const searchFilter = useMemo(
    () => buildLeadsRegistrySearchFilter(filters),
    [filters]
  );

  // Deterministic signature of the effective filters. It is the single source
  // of truth for "have the filters changed?" — the search effect compares it
  // instead of object identity, and it is what guards `SearchLeads` from being
  // re-sent on rerenders, accepted-result updates, or realtime patches.
  const searchSignature = useMemo(
    () => buildLeadsRegistrySearchSignature(searchFilter),
    [searchFilter]
  );

  const runSearch = useCallback(
    async ({ maxItems, isNextPage }: RunLeadsRegistrySearchParams) => {
      if (!activeBranchId) return;

      const requestId = ++requestIdRef.current;

      if (isNextPage) {
        setNextPageLoading(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await searchLeads({
          ActiveBranchId: activeBranchId,
          data: {
            ...searchFilter,
            MaxItems: normalizeLeadsPortionSize(maxItems),
          },
        }).unwrap();

        const responseSearchId = bigNumberToString(response.SearchId) || null;

        // Ignore stale responses; only the latest request may accept results.
        // The discarded group is queued for server-side cleanup.
        if (requestId !== requestIdRef.current) {
          if (responseSearchId) {
            queueStaleSearchId(responseSearchId);
          }

          return;
        }

        // TEMP DEBUG issue/10291 — что вернул SearchLeads: SearchId + id:имя лидов.
        const responseItems = response.Items ?? [];
        clogLeadsRegistryDebug(
          database,
          `[10291] SearchLeads: SearchId=${responseSearchId}, лидов=${responseItems.length}`,
          responseItems
            .map((lead) => `${lead.Id}:${lead.ClientName ?? '—'}`)
            .join(', ')
        );

        acceptLatestResult({
          leads: isNextPage
            ? referenceStableMergeLeads(leads, response.Items ?? [])
            : response.Items ?? [],
          totalCount: response.CountTotal ?? null,
          searchId: responseSearchId,
          searchSignature,
          maxSize: normalizeLeadsPortionSize(loadBatchSize),
          requestedMaxSize: normalizeLeadsPortionSize(maxItems),
        });
      } catch (error) {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setNextPageLoading(false);
        }

        showNotification(error, 'warning');
      }
    },
    [
      acceptLatestResult,
      activeBranchId,
      database,
      loadBatchSize,
      leads,
      queueStaleSearchId,
      searchFilter,
      searchSignature,
      searchLeads,
      setLoading,
      setNextPageLoading,
      showNotification,
    ]
  );

  const loadMoreHandler = useCallback(() => {
    if (isNextPageLoading || !hasNextPage) return;

    requestNextPage();
  }, [hasNextPage, isNextPageLoading, requestNextPage]);

  // Distinguish the table loading states from the SearchLeads lifecycle flags:
  // - `load-more`: a next-page request is in flight (rows stay, bottom skeleton);
  // - `initial`: a search is loading and no result has ever been accepted;
  // - `filter-change`: a search is loading while the accepted result still
  //   belongs to the previous filters (its signature differs from the current
  //   one), so the old rows are stale and must be replaced by a skeleton body;
  // - `idle`: nothing is loading, or a realtime patch updated rows in place.
  const loadingMode: LeadsRegistryTableLoadingMode = isNextPageLoading
    ? 'load-more'
    : isLoading && !hasAcceptedResult
    ? 'initial'
    : isLoading && acceptedSearchSignature !== searchSignature
    ? 'filter-change'
    : 'idle';

  useLeadsRegistrySearchEffects({
    activeBranchId,
    acceptedSearchSignature,
    hasAcceptedResult,
    isStale,
    refreshRequestId,
    requestedMaxSize,
    searchSignature,
    resetResult: resetLatestResult,
    runSearch,
  });

  useLeadsRegistrySearchIdCleanup({
    activeBranchId,
    pendingSearchIds: pendingDeleteSearchIds,
    onCleaned: cleanedSearchIdsHandler,
  });

  return {
    leads,
    totalCount,
    loadingMode,
    isLoadingMore: isNextPageLoading,
    hasMoreLeads: hasNextPage,
    onLoadMore: loadMoreHandler,
  };
};
